import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import LandingNavigation from '../components/LandingNavigation';
import LandingFooter from '../components/LandingFooter';
import '../styles/landing.css';

const PrivacyPolicy = () => {
    const lastUpdated = 'February 7, 2026';

    return (
        <div className="legal-page">
            <LandingNavigation />

            <div className="legal-container">
                <Link to="/" className="legal-back-link">
                    <ArrowLeft size={16} />
                    Back to Home
                </Link>

                <h1 className="legal-title">Privacy Policy</h1>
                <p className="legal-updated">Last updated: {lastUpdated}</p>

                <div className="legal-content">
                    <section className="legal-section">
                        <h2>1. Introduction</h2>
                        <p>
                            Welcome to Vitisiti ("we," "our," or "us"). We are committed to protecting your
                            privacy and personal information. This Privacy Policy explains how we collect, use,
                            disclose, and safeguard your information when you use our AI-powered creative
                            generation and marketing platform ("Service").
                        </p>
                        <p>
                            By accessing or using Vitisiti, you agree to the terms of this Privacy Policy.
                            If you do not agree with the terms, please do not access or use our Service.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>2. Information We Collect</h2>

                        <h3>2.1 Information You Provide</h3>
                        <ul>
                            <li><strong>Account Information:</strong> Name, email address, and password when you create an account.</li>
                            <li><strong>Brand Information:</strong> Brand names, logos, colors, descriptions, and other brand assets you upload.</li>
                            <li><strong>Content:</strong> Images, text, and other creative content you generate or upload.</li>
                            <li><strong>Payment Information:</strong> Billing details processed through our secure payment providers.</li>
                            <li><strong>Communications:</strong> Any messages or feedback you send to us.</li>
                        </ul>

                        <h3>2.2 Information Collected Automatically</h3>
                        <ul>
                            <li><strong>Usage Data:</strong> Pages visited, features used, time spent, and interactions with the Service.</li>
                            <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, and IP address.</li>
                            <li><strong>Cookies:</strong> We use cookies and similar technologies for authentication, preferences, and analytics.</li>
                        </ul>

                        <h3>2.3 Third-Party Platform Data</h3>
                        <p>
                            When you connect third-party platforms (such as Meta/Facebook, Google, or other
                            advertising platforms), we may receive:
                        </p>
                        <ul>
                            <li>Account identifiers and profile information</li>
                            <li>Advertising campaign performance data</li>
                            <li>Page and ad account access tokens</li>
                            <li>Audience insights and analytics</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>3. How We Use Your Information</h2>
                        <p>We use the collected information to:</p>
                        <ul>
                            <li>Provide, maintain, and improve our Service</li>
                            <li>Generate AI-powered creative content and recommendations</li>
                            <li>Manage your account and provide customer support</li>
                            <li>Process transactions and send billing information</li>
                            <li>Analyze usage patterns to enhance user experience</li>
                            <li>Send important updates about the Service</li>
                            <li>Comply with legal obligations</li>
                            <li>Detect and prevent fraud or abuse</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>4. Data Sharing and Disclosure</h2>
                        <p>We may share your information with:</p>
                        <ul>
                            <li><strong>Service Providers:</strong> Third parties that help us operate the Service (hosting, analytics, payment processing).</li>
                            <li><strong>Connected Platforms:</strong> When you authorize connections to advertising platforms like Meta.</li>
                            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights.</li>
                            <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales.</li>
                        </ul>
                        <p>
                            We do not sell your personal information to third parties for their marketing purposes.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>5. Data Retention</h2>
                        <p>
                            We retain your information for as long as your account is active or as needed to
                            provide the Service. You may request deletion of your data at any time by contacting
                            us or through your account settings.
                        </p>
                        <p>
                            Some data may be retained for legal compliance, dispute resolution, or enforcement
                            of our agreements.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>6. Data Security</h2>
                        <p>
                            We implement appropriate technical and organizational security measures to protect
                            your information, including:
                        </p>
                        <ul>
                            <li>Encryption of data in transit and at rest</li>
                            <li>Secure authentication mechanisms</li>
                            <li>Regular security assessments</li>
                            <li>Access controls and monitoring</li>
                        </ul>
                        <p>
                            However, no method of transmission over the Internet is 100% secure. We cannot
                            guarantee absolute security of your data.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>7. Your Rights and Choices</h2>
                        <p>Depending on your location, you may have the right to:</p>
                        <ul>
                            <li>Access and receive a copy of your personal data</li>
                            <li>Correct inaccurate information</li>
                            <li>Request deletion of your data</li>
                            <li>Object to or restrict processing</li>
                            <li>Data portability</li>
                            <li>Withdraw consent at any time</li>
                        </ul>
                        <p>
                            To exercise these rights, please contact us at privacy@vitisiti.com or through
                            your account settings.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>8. Third-Party Services</h2>
                        <p>
                            Our Service integrates with third-party platforms including but not limited to:
                        </p>
                        <ul>
                            <li>Meta (Facebook/Instagram) for advertising management</li>
                            <li>Google for analytics and advertising</li>
                            <li>Firebase for authentication and data storage</li>
                            <li>OpenAI for AI-powered content generation</li>
                        </ul>
                        <p>
                            These services have their own privacy policies, and we encourage you to review them.
                            We are not responsible for the privacy practices of third-party services.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>9. Children's Privacy</h2>
                        <p>
                            Our Service is not intended for children under 16 years of age. We do not knowingly
                            collect personal information from children. If you believe we have collected
                            information from a child, please contact us immediately.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>10. International Data Transfers</h2>
                        <p>
                            Your information may be transferred to and processed in countries other than your
                            own. We ensure appropriate safeguards are in place to protect your data in
                            accordance with applicable laws.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>11. Changes to This Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time. We will notify you of any
                            material changes by posting the new policy on this page and updating the "Last
                            updated" date.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>12. Data Deletion</h2>
                        <p>
                            You can request deletion of your personal data at any time. To delete your data:
                        </p>
                        <ul>
                            <li>Go to Account Settings in your dashboard</li>
                            <li>Click "Delete Account" to remove all your data</li>
                            <li>Or email us at privacy@vitisiti.com with your deletion request</li>
                        </ul>
                        <p>
                            Upon deletion, we will remove your personal data from our systems within 30 days,
                            except where retention is required by law.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>13. Contact Us</h2>
                        <p>
                            If you have questions about this Privacy Policy or our data practices, please
                            contact us at:
                        </p>
                        <div className="legal-contact">
                            <p><strong>Vitisiti</strong></p>
                            <p>Email: privacy@vitisiti.com</p>
                            <p>For data deletion requests: privacy@vitisiti.com</p>
                        </div>
                    </section>
                </div>
            </div>

            <LandingFooter />
        </div>
    );
};

export default PrivacyPolicy;
